/**
 * ActivationAuthorisationGate (AJIP-16.02.006 · WS7)
 * ---------------------------------------------------------------------
 * The single, mandatory Activation Authorisation Gate.
 *
 * Additional Founder Directive (AJIP-16.02.006):
 *   The Activation Authorisation Gate is a PROTECTED ARCHITECTURAL
 *   COMPONENT. Future implementation packages may extend gate
 *   evaluation but shall not bypass it. Any structural modification
 *   requires explicit Founder approval.
 *
 * Additional Founder Directive (AJIP-16.02.006):
 *   Activation remains deterministic. AI may recommend. AI shall never
 *   activate. Only deterministic business rules defined by the GIWAHS
 *   Canon may authorise journey activation.
 *
 * All 13 gates fail-closed on failure.
 */
import { isFeatureEnabled } from '../../lib/featureFlags';
import {
  ActivationAuthorisationError,
  CoreEngineUnavailableError,
  PermissionDeniedError,
  MissingPrerequisiteError,
  OwnershipMismatchError,
  ActivationConflictError,
} from './ResolutionActivationErrors';
import { RESOLUTION_DECISIONS } from './ResolutionDecisionContract';
import { evaluateEligibility, DECISIONS as ELIG } from '../registrationDiscovery/EligibilityEvaluator';

const GATE_IDS = Object.freeze([
  'G01_MASTER_FLAG_ENABLED',
  'G02_RESOLUTION_DECISION_PRESENT',
  'G03_RESOLUTION_RESOLVED',
  'G04_JOURNEY_REGISTERED',
  'G05_DEFINITION_VALID',
  'G06_VERSION_SUPPORTED',
  'G07_ELIGIBILITY_CONFIRMED',
  'G08_PERMISSIONS_CONFIRMED',
  'G09_PREREQUISITES_SATISFIED',
  'G10_NO_UNRESOLVED_CONFLICT',
  'G11_OWNERSHIP_SATISFIED',
  'G12_ACTIVATION_INTENT_EXPLICIT',
  'G13_CORE_ENGINE_AVAILABLE',
]);

const _fail = (gateId, message, ErrorCtor = ActivationAuthorisationError, details = {}) => {
  throw new ErrorCtor(`[${gateId}] ${message}`, { gateId, ...details });
};

const assertActivationAuthorised = ({
  request,
  resolutionDecision,
  definition,
  engine,
  registrationService,
  discoveryService,
  persistenceService, // reserved for future gates; not consulted directly here
  nowFn = () => Date.now(),
} = {}) => {
  const passed = [];

  // G01
  if (!isFeatureEnabled('JOURNEYS_ENABLED')) {
    _fail('G01_MASTER_FLAG_ENABLED', 'JOURNEYS_ENABLED is false');
  }
  passed.push('G01_MASTER_FLAG_ENABLED');

  // G02
  if (!resolutionDecision || typeof resolutionDecision !== 'object') {
    _fail('G02_RESOLUTION_DECISION_PRESENT', 'ResolutionDecision missing');
  }
  passed.push('G02_RESOLUTION_DECISION_PRESENT');

  // G03
  if (resolutionDecision.decision !== RESOLUTION_DECISIONS.RESOLVED
      && resolutionDecision.decision !== RESOLUTION_DECISIONS.RESTORATION_REQUIRED) {
    _fail('G03_RESOLUTION_RESOLVED', `ResolutionDecision is '${resolutionDecision.decision}'`);
  }
  passed.push('G03_RESOLUTION_RESOLVED');

  // G04
  if (registrationService && typeof registrationService.getRegistrationStatus === 'function') {
    const status = registrationService.getRegistrationStatus(resolutionDecision.journeyId);
    if (!status) _fail('G04_JOURNEY_REGISTERED', 'Journey not registered');
  }
  passed.push('G04_JOURNEY_REGISTERED');

  // G05
  if (!definition || typeof definition !== 'object') {
    // Try to fetch via DiscoveryService if not supplied.
    if (discoveryService && typeof discoveryService.getById === 'function') {
      definition = discoveryService.getById(resolutionDecision.journeyId);
    }
  }
  if (!definition || definition.registrationStatus !== 'registered') {
    _fail('G05_DEFINITION_VALID', 'Definition missing or not registered');
  }
  passed.push('G05_DEFINITION_VALID');

  // G06
  if (Number.isInteger(request && request.journeyVersion)
      && request.journeyVersion !== definition.journeyVersion) {
    _fail('G06_VERSION_SUPPORTED', 'Requested version does not match registered version');
  }
  passed.push('G06_VERSION_SUPPORTED');

  // G07 — re-evaluate eligibility deterministically. Defence in depth.
  const context = request && request.discoveryContext ? request.discoveryContext : null;
  const elig = evaluateEligibility({ definition, context, nowFn });
  if (elig.decision !== ELIG.ELIGIBLE) {
    switch (elig.decision) {
      case ELIG.MISSING_PERMISSION:
        _fail('G07_ELIGIBILITY_CONFIRMED', elig.reason, PermissionDeniedError, { elig });
        break;
      case ELIG.MISSING_PREREQUISITE:
        _fail('G07_ELIGIBILITY_CONFIRMED', elig.reason, MissingPrerequisiteError, { elig });
        break;
      default:
        _fail('G07_ELIGIBILITY_CONFIRMED', `Eligibility '${elig.decision}': ${elig.reason}`, ActivationAuthorisationError, { elig });
    }
  }
  passed.push('G07_ELIGIBILITY_CONFIRMED');

  // G08 — permissions superset check (defensive; eligibility already covers)
  const perms = new Set((context && context.permissions) || []);
  for (const p of definition.permissionRequirements || []) {
    if (!perms.has(p)) _fail('G08_PERMISSIONS_CONFIRMED', `Missing permission '${p}'`, PermissionDeniedError);
  }
  passed.push('G08_PERMISSIONS_CONFIRMED');

  // G09 — prerequisites (from eligibilityEvaluator, defensive re-check)
  const completed = new Set((context && context.completedJourneys) || []);
  const prereqs = (definition.entryConditions || []).filter((c) => typeof c === 'string' && c.startsWith('requires:'));
  for (const req of prereqs) {
    const id = req.slice('requires:'.length);
    if (!completed.has(id)) _fail('G09_PREREQUISITES_SATISFIED', `Missing prerequisite '${id}'`, MissingPrerequisiteError);
  }
  passed.push('G09_PREREQUISITES_SATISFIED');

  // G10 — no unresolved conflict.
  if (resolutionDecision.conflictStatus && resolutionDecision.conflictStatus !== 'none') {
    _fail('G10_NO_UNRESOLVED_CONFLICT', `Conflict: ${resolutionDecision.conflictStatus}`, ActivationConflictError, {
      conflictStatus: resolutionDecision.conflictStatus,
    });
  }
  passed.push('G10_NO_UNRESOLVED_CONFLICT');

  // G11 — ownership boundaries. Today only the request principal is
  // consulted against any pre-existing engine context. Persistence
  // ownership is separately enforced by the Persistence Framework
  // during restore().
  const principalRef = (request && request.principalContext && request.principalContext.principalRef) || null;
  const existing = engine && typeof engine.contextOf === 'function'
    ? engine.contextOf(definition.journeyId)
    : null;
  if (existing && principalRef !== null && existing.flagStatus && existing.flagStatus.principalRef !== undefined
      && existing.flagStatus.principalRef !== principalRef) {
    _fail('G11_OWNERSHIP_SATISFIED', 'Existing context owned by different principal', OwnershipMismatchError);
  }
  passed.push('G11_OWNERSHIP_SATISFIED');

  // G12 — explicit intent.
  if (!request || request.activationIntent !== true) {
    _fail('G12_ACTIVATION_INTENT_EXPLICIT', 'activationIntent must be explicitly true');
  }
  passed.push('G12_ACTIVATION_INTENT_EXPLICIT');

  // G13 — Core Engine available.
  if (!engine || engine.enabled !== true) {
    _fail('G13_CORE_ENGINE_AVAILABLE', 'Core Engine not available', CoreEngineUnavailableError);
  }
  passed.push('G13_CORE_ENGINE_AVAILABLE');

  return Object.freeze({ ok: true, gatesPassed: Object.freeze(passed) });
};

export { GATE_IDS, assertActivationAuthorised };
